import React, { useState, useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import ReactPlayer from 'react-player';
import screenful from 'screenfull';
import Controls from './components/Controls';
import { useVideoContext } from '@/app/contexts/VideoContext';
// import { Box, CircularProgress } from '@mui/material';
// import { PlayIcon } from '@/public/icon';
interface StylesEmbedded {
    reactPlayers: React.CSSProperties;
    reactPlayerWrappers: React.CSSProperties;
}
const PlayerWrapper = styled('div')(({ theme }) => ({
    width: '100%',
    height: 'auto',
    position: 'relative',
}));

const stylesEmbedded: StylesEmbedded = {
    reactPlayers: {
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        height: '355px',
        width: '100%',
    },
    reactPlayerWrappers: {
        paddingTop: '56.25%',
        minHeight: '255px',
    },
};
interface JoyPlayerProps {
    src: string;
    controls?: boolean;
    width?: string | number;
    height?: string | number;
    uniqueId?: string;
    onReady?: (index: any) => void;
    onHandleMediaVideoProcess?: (allowTouchMove: boolean) => void;
    showCustomControls?: boolean;
    isResizeWithOutSwiper?: boolean;
}

const JoyPlayer: React.FC<JoyPlayerProps> = ({
    src,
    controls = true,
    uniqueId,
    showCustomControls = false,
    isResizeWithOutSwiper = false,
    width = '100%',
    height = '100%',
    onReady,
    onHandleMediaVideoProcess,
}) => {
    const [timeDisplayFormat, setTimeDisplayFormat] = useState('normal');
    const [isPlaying, setIsPlaying] = useState(false);
    const [state, setState] = useState({
        pip: false,
        playing: false,
        controls: false,
        light: true,
        muted: true,
        played: 0,
        duration: 0,
        playbackRate: 1.0,
        volume: 1,
        loop: false,
        seeking: false,
        isLoading: false,
    });
    const playerRef = useRef<ReactPlayer>(null);
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const controlsRef = useRef<HTMLDivElement>(null);
    const { playing, muted, loop, playbackRate, played, volume, isLoading, seeking } = state;
    const { currentVideoId, currentTime: currentTimeContext, setCurrentVideo, pauseCurrentVideo } = useVideoContext();

    const [currentUrl, setCurrentUrl] = useState(src);
    const handlePlayPause = () => {
        setState({ ...state, playing: !state.playing, isLoading: false });
    };

    useEffect(() => {
        return () => {
            //avoid play many video , clear all video is playing
            if (currentVideoId) {
                pauseCurrentVideo();
            }
        };
    }, []);

    const handlePlay = () => {
        if (currentVideoId) {
            pauseCurrentVideo();
        }
        setCurrentVideo(uniqueId ?? '', playerRef.current?.getCurrentTime() || 0);
    };

    useEffect(() => {
        if (src !== currentUrl) {
            setCurrentUrl(src);
        }
    }, [src]);

    const handleRewind = () => {
        playerRef.current?.seekTo(playerRef.current.getCurrentTime() - 10);
    };

    const handleFastForward = () => {
        playerRef.current?.seekTo(playerRef.current.getCurrentTime() + 10);
    };

    const handleProgress = (changeState: any) => {
        if (!state.seeking) {
            setState({ ...state, ...changeState });
        }
    };

    const handleSeekChange = (e: Event, newValue: number | number[]): void => {
        if (typeof newValue === 'number') {
            const playedValue = newValue / 100;
            setState((prevState) => ({
                ...prevState,
                played: playedValue,
            }));
        } else {
            console.warn('Unexpected array value in handleSeekChange');
        }
    };
    const handleSeekMouseDown = () => {
        setState({ ...state, seeking: true });
    };

    const handleSeekMouseUp = (e: Event, newValue: number | number[]) => {
        setState({ ...state, seeking: false });
        playerRef.current?.seekTo(parseFloat(String(newValue)) / 100, 'fraction');
    };

    const handleDuration = (duration: number) => {
        setState({ ...state, duration });
    };

    const handleVolumeSeekDown = (e: Event, newValue: number | number[]) => {
        setState({ ...state, seeking: false, volume: parseFloat(String(newValue)) / 100 });
    };

    const handleVolumeChange = (e: Event, newValue: number | number[]) => {
        setState({
            ...state,
            volume: parseFloat(String(newValue)) / 100,
            muted: newValue === 0,
        });
    };

    const toggleFullScreen = () => {
        if (screenful.isEnabled && playerContainerRef.current) {
            screenful.toggle(playerContainerRef.current);
        }
    };

    const onMoveSlider = () => {
        onHandleMediaVideoProcess?.(false);
    };

    const onLeaveSlider = () => {
        onHandleMediaVideoProcess?.(true);
    };

    const handleMouseMove = () => {
        if (controlsRef.current) {
            controlsRef.current.style.visibility = 'visible';
            controlsRef.current.style.opacity = '1';
            controlsRef.current.style.transitionDelay = '0s, 0s';
        }
    };

    const handleMouseLeave = () => {
        if (controlsRef.current) {
            controlsRef.current.style.opacity = '0';
            controlsRef.current.style.transitionDelay = '0ms, 200ms';
            if (controlsRef.current) {
                controlsRef.current.style.visibility = 'hidden';
            }
        }
    };

    const handleDisplayFormat = () => {
        setTimeDisplayFormat(timeDisplayFormat === 'normal' ? 'remaining' : 'normal');
    };

    const handlePlaybackRate = (rate: number) => {
        setState({ ...state, playbackRate: rate });
    };

    const handleMute = () => {
        setState({ ...state, muted: !state.muted });
    };

    const format = (seconds: number) => {
        if (isNaN(seconds)) {
            return `00:00`;
        }
        const date = new Date(seconds * 1000);
        const hh = date.getUTCHours();
        const mm = date.getUTCMinutes();
        const ss = date.getUTCSeconds().toString().padStart(2, '0');
        if (hh) {
            return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`;
        }
        return `${mm}:${ss}`;
    };

    const currentTime = playerRef.current ? playerRef.current.getCurrentTime() : 0;

    const duration = playerRef.current ? playerRef.current.getDuration() : 0;

    const elapsedTime = timeDisplayFormat === 'normal' ? format(currentTime) : `-${format(duration - currentTime)}`;

    const totalDuration = format(duration);

    return (
        <>
            <PlayerWrapper
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                ref={playerContainerRef}
                sx={isResizeWithOutSwiper ? stylesEmbedded.reactPlayerWrappers : {}}
            >
                <ReactPlayer
                    ref={playerRef}
                    width={width}
                    height={height}
                    url={currentUrl}
                    playing={currentVideoId === uniqueId}
                    controls={false}
                    onReady={onReady}
                    onBuffer={() => setState((s) => ({ ...s, isLoading: true }))}
                    onBufferEnd={() => setState((s) => ({ ...s, isLoading: false }))}
                    loop={loop}
                    onPlay={handlePlay}
                    // onPause={handlePause}
                    // onEnded={() => console.log('Video has ended')}
                    playbackRate={playbackRate}
                    volume={volume}
                    muted={muted}
                    onProgress={handleProgress}
                    config={{
                        youtube: {
                            playerVars: {
                                controls: 1,
                                modestbranding: 1,
                                rel: 0,
                                showinfo: 0,
                                disablekb: 1,
                                iv_load_policy: 3,
                                cc_load_policy: 0,
                                autoplay: 0,
                            },
                        },
                        file: {
                            attributes: {
                                preload: 'metadata',
                            },
                        },
                    }}
                    style={isResizeWithOutSwiper ? stylesEmbedded.reactPlayers : { display: 'flex' }}
                />

                {/* {isLoading && (
                    <Box
                        sx={{
                            zIndex: 9999,
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )} */}

                {showCustomControls ? (
                    <Controls
                        ref={controlsRef}
                        onSeek={handleSeekChange}
                        onSeekMouseDown={handleSeekMouseDown}
                        onSeekMouseUp={handleSeekMouseUp}
                        onDuration={handleDuration}
                        onRewind={handleRewind}
                        onPlayPause={handlePlayPause}
                        onFastForward={handleFastForward}
                        playing={playing}
                        isLoading={isLoading}
                        played={played}
                        elapsedTime={elapsedTime}
                        totalDuration={totalDuration}
                        onMute={handleMute}
                        muted={muted}
                        onVolumeChange={handleVolumeChange}
                        onVolumeSeekDown={handleVolumeSeekDown}
                        onChangeDisplayFormat={handleDisplayFormat}
                        playbackRate={playbackRate}
                        onPlaybackRateChange={handlePlaybackRate}
                        onToggleFullScreen={toggleFullScreen}
                        volume={volume}
                        onBookmark={() => null}
                        handleMouseLeaveOnSlider={onLeaveSlider}
                        handleMouseMoveOnSlider={onMoveSlider}
                    />
                ) : null}
            </PlayerWrapper>
        </>
    );
};

export default React.memo(JoyPlayer);
